import { RadioGroup } from '@headlessui/react';
import classNames from 'classnames';
import {
  UseFormRegister,
  Controller,
  useFormContext,
  FieldError,
} from 'react-hook-form';
import { BinarySelectOption, BinarySelectValues } from '@insurgrid/common';
import { useState } from 'react';
import { get } from 'lodash';
import { FormControl } from './FormControl';

export interface BasicBinarySelectProps {
  values: BinarySelectOption[];
  defaultValue: BinarySelectOption | null;
  onChange: (value: boolean) => void;
  'data-test-id'?: string;
  optionClassName?: string;
}

export function BinarySelect(props: BasicBinarySelectProps) {
  // selected is the label of the binary select or null
  // if a value is passed in, set the initial state to the options label
  const [selected, setSelected] = useState<string | null>(
    props.defaultValue?.label ?? null,
  );

  return (
    <RadioGroup
      value={selected}
      onChange={(selectedOptionLabel: string) => {
        setSelected(selectedOptionLabel);
        const selectedOption = props.values.find(
          (value) => value.label === selectedOptionLabel,
        );

        props.onChange(!!selectedOption?.value);
      }}
      data-test-id={props['data-test-id']}
    >
      <RadioGroup.Label className="sr-only">Server size</RadioGroup.Label>
      <div className="space-y-4">
        {Object.values(props.values).map((value: BinarySelectOption) => (
          <RadioGroup.Option
            key={value.label}
            value={value.label}
            className={({ checked, active }) =>
              classNames(
                'relative block bg-white border rounded-lg shadow-sm px-6 py-4 cursor-pointer sm:flex sm:justify-between focus:outline-none transition-all  hover:bg-gray-50',
                checked ? 'border-transparent !bg-white' : 'border-gray-300',
                active ? 'border-blue-light ring-2 ring-blue bg-white' : '',
                props.optionClassName,
              )
            }
            data-test-id={`binary-select-option-${value.label}`}
          >
            {({ active, checked }) => (
              <>
                <span className="flex items-center">
                  <span className="text-sm flex flex-col">
                    <RadioGroup.Label
                      as="span"
                      className="font-medium text-gray-900"
                    >
                      {value.label}
                    </RadioGroup.Label>
                  </span>
                </span>
                <span
                  className={classNames(
                    active ? 'border' : 'border-2',
                    checked ? 'border-blue' : 'border-transparent',
                    'absolute -inset-px rounded-lg pointer-events-none',
                  )}
                  aria-hidden="true"
                />
              </>
            )}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  );
}
export interface BinarySelectProps {
  name: string;
  register?: UseFormRegister<any>;
  label?: string;
  values: BinarySelectValues;
  defaultValue?: boolean;
}

export function FormBinarySelect(props: BinarySelectProps) {
  const {
    formState: { errors },
    control,
  } = useFormContext();

  const error = get(errors, props.name);
  return (
    <FormControl
      name={props.name}
      label={props.label}
      error={error as FieldError}
    >
      <Controller
        control={control}
        defaultValue={props.defaultValue}
        name={props.name}
        render={({ field }) => {
          const defaultValue = props.values?.find(
            (val) => val.value === field.value,
          );

          return (
            <BinarySelect
              values={props.values}
              defaultValue={defaultValue ?? null}
              onChange={(value) => {
                field.onChange(value);
              }}
              data-test-id="binary-select"
              optionClassName={classNames(
                error && 'border-red-400 text-red-400',
              )}
            />
          );
        }}
      />
    </FormControl>
  );
}
